import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import {
  AppBadge, AppButton, AppCard, AppField, AppInput, AppModal, AppSelect, AppTextarea,
} from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";
import { readEntityPrefill } from "../../../shared/navigation/entityPrefill";

type TicketStatus = "open" | "in_progress" | "resolved" | "closed";
type TicketType = "technical" | "payment" | "complaint" | "general";
type TicketPriority = "low" | "medium" | "high" | "critical";

type Ticket = {
  id: number;
  customer_id: number | null;
  device_serial: string;
  ticket_type: TicketType;
  title: string;
  description: string;
  priority: TicketPriority;
  status: TicketStatus;
  resolution: string;
  assigned_to: number | null;
  created_by: number | null;
  created_at: string;
  updated_at: string | null;
  resolved_at: string | null;
};

const STATUS_FILTERS: { value: TicketStatus | ""; label: string }[] = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const TYPE_OPTIONS: { value: TicketType; label: string }[] = [
  { value: "general", label: "General" },
  { value: "technical", label: "Device fault" },
  { value: "payment", label: "Payment" },
  { value: "complaint", label: "Complaint" },
];

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

function statusBadge(status: TicketStatus) {
  const map: Record<TicketStatus, { tone: "success" | "warning" | "error" | "info" | "neutral"; label: string }> = {
    open: { tone: "warning", label: "Open" },
    in_progress: { tone: "info", label: "In Progress" },
    resolved: { tone: "success", label: "Resolved" },
    closed: { tone: "neutral", label: "Closed" },
  };
  const { tone, label } = map[status];
  return <AppBadge tone={tone}>{label}</AppBadge>;
}

function priorityBadge(priority: TicketPriority) {
  const map: Record<TicketPriority, "success" | "warning" | "error" | "info" | "neutral"> = {
    low: "neutral", medium: "info", high: "warning", critical: "error",
  };
  return <AppBadge tone={map[priority]}>{priority}</AppBadge>;
}

function CreateTicketModal({
  open,
  onClose,
  onCreated,
  prefill,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  prefill?: { customer_id?: string | null; device_serial?: string | null };
}) {
  const [form, setForm] = useState({
    title: "", description: "", ticket_type: "general" as TicketType,
    priority: "medium" as TicketPriority, device_serial: "", customer_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm({
      title: "",
      description: "",
      ticket_type: "general",
      priority: "medium",
      device_serial: prefill?.device_serial ?? "",
      customer_id: prefill?.customer_id ?? "",
    });
    setErr("");
  }, [open, prefill?.customer_id, prefill?.device_serial]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setErr("Title is required");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post("/tickets", {
        title: form.title.trim(),
        description: form.description.trim(),
        ticket_type: form.ticket_type,
        priority: form.priority,
        device_serial: form.device_serial.trim() || undefined,
        customer_id: form.customer_id ? Number(form.customer_id) : undefined,
      });
      onCreated();
      onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to create ticket");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} title="New Support Ticket">
      <form onSubmit={submit}>
        <AppField label="Title">
          <AppInput
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            disabled={busy}
            autoFocus
            placeholder="Short summary of the issue"
          />
        </AppField>
        <AppField label="Description">
          <AppTextarea
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            disabled={busy}
            placeholder="Describe the issue"
          />
        </AppField>
        <div className="pg-two-col-form">
          <AppField label="Type">
            <AppSelect value={form.ticket_type} onChange={(e) => set("ticket_type", e.target.value)} disabled={busy}>
              {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </AppSelect>
          </AppField>
          <AppField label="Priority">
            <AppSelect value={form.priority} onChange={(e) => set("priority", e.target.value)} disabled={busy}>
              {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </AppSelect>
          </AppField>
        </div>
        <div className="pg-two-col-form">
          <AppField label="Device serial (optional)">
            <AppInput
              value={form.device_serial}
              onChange={(e) => set("device_serial", e.target.value)}
              disabled={busy}
              placeholder="e.g. SN-00123"
            />
          </AppField>
          <AppField label="Customer ID (optional)">
            <AppInput
              type="number"
              value={form.customer_id}
              onChange={(e) => set("customer_id", e.target.value)}
              disabled={busy}
            />
          </AppField>
        </div>
        {err && <span className="error" role="alert">{err}</span>}
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14} /> Creating…</> : "Create Ticket"}</AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

function ResolveModal({ ticket, onClose, onResolved }: { ticket: Ticket | null; onClose: () => void; onResolved: () => void }) {
  const [resolution, setResolution] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { setResolution(""); setErr(""); }, [ticket?.id]);

  if (!ticket) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolution.trim()) {
      setErr("Resolution note is required");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.put(`/tickets/${ticket.id}`, { status: "resolved", resolution: resolution.trim() });
      onResolved();
      onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to resolve ticket");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={!!ticket} onClose={onClose} title={`Resolve — ${ticket.title}`}>
      <form onSubmit={submit}>
        <AppField label="Resolution note">
          <AppTextarea
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            disabled={busy}
            placeholder="What fixed the issue?"
            autoFocus
          />
        </AppField>
        {err && <span className="error" role="alert">{err}</span>}
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14} /> Saving…</> : "Mark Resolved"}</AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

export function TicketList() {
  const [searchParams] = useSearchParams();
  const urlPrefill = readEntityPrefill(searchParams);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [pageState, setPageState] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [statusFilter, setStatusFilter] = useState<TicketStatus | "">("");
  const [createOpen, setCreateOpen] = useState(false);
  const [resolveTicket, setResolveTicket] = useState<Ticket | null>(null);
  const [actionBusy, setActionBusy] = useState<number | null>(null);
  const [autoOpenDone, setAutoOpenDone] = useState(false);

  useEffect(() => {
    if (autoOpenDone || !urlPrefill.customer_id) return;
    setCreateOpen(true);
    setAutoOpenDone(true);
  }, [urlPrefill.customer_id, autoOpenDone]);

  const load = async () => {
    setPageState("loading");
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : "";
      const d = await api.get<{ tickets: Ticket[] }>(`/tickets${qs}`);
      setTickets(d.tickets ?? []);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load tickets");
      setPageState("error");
    }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const setInProgress = async (id: number) => {
    setActionBusy(id);
    try { await api.put(`/tickets/${id}`, { status: "in_progress" }); await load(); }
    catch { /* row stays actionable */ }
    finally { setActionBusy(null); }
  };

  const columns: ColumnDef<Ticket, unknown>[] = [
    { accessorKey: "title", header: "Title" },
    { id: "type", header: "Type", accessorFn: (r) => r.ticket_type.replace(/_/g, " ") },
    { id: "priority", header: "Priority", cell: ({ row }) => priorityBadge(row.original.priority) },
    { id: "device_serial", header: "Device", accessorFn: (r) => r.device_serial || "—" },
    { id: "status", header: "Status", cell: ({ row }) => statusBadge(row.original.status) },
    { id: "created_at", header: "Opened", accessorFn: (r) => new Date(r.created_at).toLocaleDateString("en-GB") },
    {
      id: "actions", header: "", enableSorting: false,
      cell: ({ row }) => {
        const t = row.original;
        if (t.status === "open") {
          return (
            <AppButton type="button" variant="secondary" disabled={actionBusy === t.id} onClick={(e) => { e.stopPropagation(); setInProgress(t.id); }}>
              {actionBusy === t.id ? <IconSpinner size={12} /> : "Start Work"}
            </AppButton>
          );
        }
        if (t.status === "in_progress") {
          return (
            <AppButton type="button" onClick={(e) => { e.stopPropagation(); setResolveTicket(t); }}>
              Resolve
            </AppButton>
          );
        }
        return null;
      },
    },
  ];

  const listStatus =
    pageState === "success" && tickets.length === 0 ? "empty" : pageState;

  return (
    <div>
      <CreateTicketModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
        prefill={{
          // EntityPrefill allows number|string; CreateTicketModal form fields are strings.
          customer_id:
            urlPrefill.customer_id != null ? String(urlPrefill.customer_id) : undefined,
          device_serial: urlPrefill.device_serial,
        }}
      />
      <ResolveModal ticket={resolveTicket} onClose={() => setResolveTicket(null)} onResolved={load} />

      <PageHeader
        title="Support Tickets"
        subtitle="Statuses: Open → In Progress → Resolved → Closed (use Resolved for finished work — there is no separate Complete status)."
        actions={<AppButton type="button" onClick={() => setCreateOpen(true)}>New Ticket</AppButton>}
      />

      <div className="pg-filter-chips">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`pg-chip${statusFilter === f.value ? " active" : ""}`}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No tickets"
        emptyHint={statusFilter ? "No tickets match this filter." : "Create a ticket to log an issue."}
      >
        <AppCard>
          <div className="pg-table-meta"><span>{tickets.length} ticket{tickets.length !== 1 ? "s" : ""}</span></div>
          <AppDataTable data={tickets} columns={columns} />
        </AppCard>
      </PageState>
    </div>
  );
}
